/* eslint-disable @typescript-eslint/no-unused-vars */
// ============================================================
// SIMADCA v3.0 – ReportsPanel.tsx FINAL
// Raport custom cu opțiuni reale: senzori, parametri, interval
// ============================================================

import React, { useState } from 'react';
import { Select, Button, Modal, Form, Input, Checkbox, Switch, Steps, Progress, message, DatePicker } from 'antd';
import {
  FileTextOutlined, DownloadOutlined, PlusOutlined,
  CheckCircleOutlined, ClockCircleOutlined,
  FilePdfOutlined, FileExcelOutlined, EyeOutlined,
  ReloadOutlined, BarChartOutlined, CalendarOutlined, UserOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer,
} from 'recharts';
import dayjs from 'dayjs';
import { jsPDF } from 'jspdf';

import { mockReports, dailyAlertStats, mockSensors } from '../../data/mockData';
import { useSimulatorData } from '../../context/SimulatorContext';
import { Report, ReportType, ReportStatus, SensorData, Alert } from '../../types';
import { useAuth } from '../../context/AuthContext';

const { Option } = Select;
const { RangePicker } = DatePicker;

// ─── Constante ────────────────────────────────────────────

const TYPE_COLOR: Record<ReportType, string> = {
  daily: '#6366f1', weekly: '#06b6d4', monthly: '#10b981', custom: '#f59e0b',
};
const TYPE_BG: Record<ReportType, string> = {
  daily: '#eef2ff', weekly: '#ecfeff', monthly: '#d1fae5', custom: '#fef3c7',
};
const TYPE_LABEL: Record<ReportType, string> = {
  daily: 'Zilnic', weekly: 'Săptămânal', monthly: 'Lunar', custom: 'Personalizat',
};
const STAT_COLOR: Record<ReportStatus, string> = {
  generated: '#10b981', pending: '#f59e0b', failed: '#ef4444',
};
const STAT_LABEL: Record<ReportStatus, string> = {
  generated: 'Generat', pending: 'În curs', failed: 'Eșuat',
};

// Parametri unici
const ALL_PARAMS = Array.from(new Set(mockSensors.map(s => s.parameter)));

interface LiveData {
  sensors: SensorData[]; alerts: Alert[];
  sensorStatusStats: { online: number; warning: number; offline: number; calibrating: number };
}

// ─── Export CSV ───────────────────────────────────────────

const exportCSV = (report: Report, live: LiveData) => {
  const sensorsToExport = report.selectedSensors?.length
    ? live.sensors.filter(s => report.selectedSensors!.includes(s.id))
    : live.sensors;
  const filtered = report.selectedParams?.length
    ? sensorsToExport.filter(s => report.selectedParams!.includes(s.parameter))
    : sensorsToExport;

  const header = 'Senzor,Tip,Parametru,Valoare,Unitate,LOD,Max Admis,Status,Locatie,Ferma,Baterie%,Semnal%,Timestamp';
  const rows   = filtered.map(s =>
    `${s.name},${s.type},${s.parameter},${s.value},${s.unit},${s.lod},${s.maxAllowed},${s.status},"${s.location.label}","${s.farmName ?? ''}",${s.batteryLevel ?? ''},${s.signalStrength ?? ''},${s.timestamp}`
  );
  const csv    = [header, ...rows].join('\n');
  const blob   = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const url    = URL.createObjectURL(blob);
  const a      = document.createElement('a');
  a.href = url; a.download = `${report.title.replace(/\s+/g, '_')}.csv`; a.click();
  URL.revokeObjectURL(url);
  message.success('CSV exportat!');
};

// ─── Export PDF ───────────────────────────────────────────

const exportPDF = (report: Report, live: LiveData) => {
  const doc  = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W    = doc.internal.pageSize.getWidth();
  const now  = new Date().toLocaleString('ro-RO');

  doc.setFillColor(79, 70, 229); doc.rect(0, 0, W, 38, 'F');
  doc.setFillColor(99, 102, 241); doc.rect(0, 30, W, 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18); doc.setFont('helvetica', 'bold'); doc.text('SIMADCA v3.0', 14, 14);
  doc.setFontSize(8); doc.setFont('helvetica', 'normal');
  doc.text('Sistem de Monitorizare si Alarmare pentru Determinarea Contaminarilor din Ape', 14, 21);
  doc.text(`INFOSEC CENTER SRL + INFLPR  |  ${now}`, 14, 27);

  doc.setTextColor(15, 23, 42); doc.setFontSize(15); doc.setFont('helvetica', 'bold');
  doc.text(report.title, 14, 50);
  doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(100, 116, 139);
  doc.text(`Perioada: ${report.period}  |  Generat de: ${report.generatedBy}  |  ${new Date(report.generatedAt).toLocaleDateString('ro-RO')}`, 14, 57);
  doc.setDrawColor(226, 232, 240); doc.line(14, 62, W - 14, 62);

  // Summary boxes
  const boxes = [
    { l: 'Senzori', v: report.sensorCount },
    { l: 'Alerte', v: report.alertCount },
    { l: 'Online', v: live.sensorStatusStats.online },
    { l: 'Offline', v: live.sensorStatusStats.offline },
  ];
  boxes.forEach((b, i) => {
    const x = 14 + i * 46;
    doc.setFillColor(248, 250, 252); doc.roundedRect(x, 66, 42, 18, 2, 2, 'F');
    doc.setFontSize(14); doc.setFont('helvetica', 'bold'); doc.setTextColor(99, 102, 241);
    doc.text(String(b.v), x + 21, 77, { align: 'center' });
    doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(100, 116, 139);
    doc.text(b.l, x + 21, 82, { align: 'center' });
  });

  // Sensors table
  let y = 92;
  const sensorsToShow = (report.selectedSensors?.length ? live.sensors.filter(s => report.selectedSensors!.includes(s.id)) : live.sensors)
    .filter(s => !report.selectedParams?.length || report.selectedParams.includes(s.parameter));

  doc.setFillColor(248, 250, 252); doc.rect(14, y, W - 28, 7, 'F');
  doc.setFontSize(7.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(100, 116, 139);
  ['Senzor', 'Parametru', 'Valoare', 'LOD', 'Status', 'Locatie'].forEach((h, i) => {
    doc.text(h, 15 + i * 30, y + 4.5);
  });
  y += 8;

  doc.setFont('helvetica', 'normal'); doc.setFontSize(7);
  sensorsToShow.slice(0, 28).forEach((s, idx) => {
    if (y > 270) { doc.addPage(); y = 16; }
    if (idx % 2 === 0) { doc.setFillColor(252, 252, 253); doc.rect(14, y - 1, W - 28, 5.5, 'F'); }
    const col = s.status === 'warning' ? [245, 158, 11] : s.status === 'offline' ? [239, 68, 68] : [16, 185, 129];
    [s.name, s.parameter, `${s.value} ${s.unit}`, String(s.lod), s.status, s.location.label.slice(0, 22)].forEach((v, ci) => {
      if (ci === 4) doc.setTextColor(...(col as [number,number,number]));
      else doc.setTextColor(15, 23, 42);
      doc.text(v.length > 18 ? v.slice(0, 17) + '…' : v, 15 + ci * 30, y + 3.5);
    });
    y += 5.5;
  });

  // Footer
  doc.setTextColor(148, 163, 184); doc.setFontSize(7);
  doc.text('SIMADCA v3.0 — INFOSEC CENTER SRL + INFLPR — Confidențial', W / 2, 290, { align: 'center' });

  doc.save(`${report.title.replace(/\s+/g, '_')}.pdf`);
  message.success('PDF generat!');
};

// ─── Report Row ───────────────────────────────────────────

const ReportRow: React.FC<{ report: Report; onPreview: () => void; live: LiveData }> =
  ({ report: r, onPreview, live }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', borderBottom: '1px solid #f8fafc', transition: 'background 0.15s' }}
    onMouseEnter={e => (e.currentTarget as HTMLDivElement).style.background = '#fafbff'}
    onMouseLeave={e => (e.currentTarget as HTMLDivElement).style.background = 'transparent'}
  >
    <div style={{ width: 40, height: 40, borderRadius: 10, flexShrink: 0, background: TYPE_BG[r.type], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: TYPE_COLOR[r.type] }}>
      <FilePdfOutlined />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title}</span>
        <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 99, background: TYPE_BG[r.type], color: TYPE_COLOR[r.type], flexShrink: 0 }}>{TYPE_LABEL[r.type]}</span>
        <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 99, background: `${STAT_COLOR[r.status]}15`, color: STAT_COLOR[r.status], flexShrink: 0 }}>{STAT_LABEL[r.status]}</span>
      </div>
      <div style={{ fontSize: 11, color: '#94a3b8', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <span><CalendarOutlined style={{ fontSize: 10 }} /> {r.period}</span>
        <span><UserOutlined style={{ fontSize: 10 }} /> {r.generatedBy}</span>
        <span>{r.sensorCount} senzori · {r.alertCount} alerte · {r.fileSize}</span>
        {r.selectedParams?.length ? <span style={{ color: '#6366f1' }}>Parametri: {r.selectedParams.join(', ')}</span> : null}
      </div>
    </div>
    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
      {r.status === 'generated' && (
        <>
          <button onClick={onPreview} style={{ padding: '6px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', cursor: 'pointer', fontFamily: 'inherit' }}>
            <EyeOutlined />
          </button>
          <button onClick={() => exportPDF(r, live)} style={{ padding: '6px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600, border: '1px solid #fecaca', background: '#fff5f5', color: '#ef4444', cursor: 'pointer', fontFamily: 'inherit' }}>
            <FilePdfOutlined />
          </button>
          <button onClick={() => exportCSV(r, live)} style={{ padding: '6px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600, border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#059669', cursor: 'pointer', fontFamily: 'inherit' }}>
            <FileExcelOutlined />
          </button>
        </>
      )}
      {r.status === 'pending' && <span style={{ fontSize: 11, color: '#f59e0b' }}>Se generează…</span>}
    </div>
  </div>
);

// ─── Modal generare raport custom ─────────────────────────

const GenerateModal: React.FC<{
  open: boolean; onClose: () => void;
  onGenerate: (report: Report) => void; live: LiveData;
}> = ({ open, onClose, onGenerate, live }) => {
  const { user } = useAuth();
  const [step, setStep]     = useState(0);
  const [form] = Form.useForm();
  const [progress, setProgress] = useState(0);
  const [done, setDone]         = useState(false);
  const [newRep, setNewRep]     = useState<Report | null>(null);

  const handleClose = () => { setStep(0); setProgress(0); setDone(false); setNewRep(null); form.resetFields(); onClose(); };

  const handleGenerate = () => {
    form.validateFields().then(vals => {
      const rType   = vals.reportType as ReportType;
      const isPeriod = vals.period && vals.period.length === 2;
      const period  = isPeriod
        ? `${vals.period[0].format('DD.MM.YYYY')} – ${vals.period[1].format('DD.MM.YYYY')}`
        : rType === 'daily' ? new Date().toLocaleDateString('ro-RO') : rType === 'weekly' ? 'Săptămâna curentă' : 'Luna curentă';

      const selectedS = vals.sensors?.length ? vals.sensors : undefined;
      const selectedP = vals.params?.length  ? vals.params  : undefined;
      const sensCount = selectedS ? selectedS.length : live.sensors.length;
      const alertsCount = live.alerts.filter(a => a.status !== 'resolved').length;

      const rep: Report = {
        id: `r-${Date.now()}`,
        title: vals.title || `Raport ${TYPE_LABEL[rType]} – ${new Date().toLocaleDateString('ro-RO')}`,
        type: rType, status: 'pending',
        generatedAt: new Date().toISOString(),
        generatedBy: user?.name ?? 'Sistem',
        period, sensorCount: sensCount, alertCount: alertsCount,
        fileSize: `${(sensCount * 0.12 + 0.8).toFixed(1)} MB`,
        selectedSensors: selectedS, selectedParams: selectedP,
        includeCharts:     vals.includeCharts ?? true,
        includeCompliance: vals.includeCompliance ?? true,
      };

      onGenerate(rep);
      setStep(2); setProgress(0);
      const iv = setInterval(() => {
        setProgress(p => {
          if (p >= 100) { clearInterval(iv); setDone(true); setNewRep({ ...rep, status: 'generated' }); return 100; }
          return p + Math.random() * 18 + 5;
        });
      }, 180);
    });
  };

  return (
    <Modal open={open} onCancel={handleClose} footer={null} width={600} title={null} styles={{ body: { padding: 0 } }}>
      <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>Generează raport nou</div>
        <Steps current={step} size="small" items={[{ title: 'Tip' }, { title: 'Configurare' }, { title: 'Generare' }]} />
      </div>

      <div style={{ padding: '20px 24px' }}>
        {step < 2 ? (
          <Form form={form} layout="vertical">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Form.Item name="reportType" label="Tip raport" rules={[{ required: true }]} initialValue="daily">
                <Select>
                  {(Object.keys(TYPE_LABEL) as ReportType[]).map(t => (
                    <Option key={t} value={t}>
                      <span style={{ color: TYPE_COLOR[t], fontWeight: 600 }}>{TYPE_LABEL[t]}</span>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="title" label="Titlu (opțional)">
                <Input placeholder="Titlu personalizat…" />
              </Form.Item>
            </div>

            {/* Opțiuni custom */}
            <Form.Item noStyle shouldUpdate={(p, c) => p.reportType !== c.reportType}>
              {({ getFieldValue }) => getFieldValue('reportType') === 'custom' && (
                <>
                  <Form.Item name="period" label="Interval dată">
                    <RangePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
                  </Form.Item>
                  <Form.Item name="sensors" label="Senzori incluși (gol = toți)">
                    <Select mode="multiple" placeholder="Selectează senzori…" allowClear maxTagCount={4}>
                      {mockSensors.map(s => <Option key={s.id} value={s.id}>{s.name} — {s.parameter}</Option>)}
                    </Select>
                  </Form.Item>
                  <Form.Item name="params" label="Parametri incluși (gol = toți)">
                    <Select mode="multiple" placeholder="Selectează parametri…" allowClear>
                      {ALL_PARAMS.map(p => <Option key={p} value={p}>{p}</Option>)}
                    </Select>
                  </Form.Item>
                </>
              )}
            </Form.Item>

            <div style={{ display: 'flex', gap: 20 }}>
              <Form.Item name="includeCharts" label="Include grafice" valuePropName="checked" initialValue={true}>
                <Switch size="small" />
              </Form.Item>
              <Form.Item name="includeCompliance" label="Include conformitate UE" valuePropName="checked" initialValue={true}>
                <Switch size="small" />
              </Form.Item>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={handleClose} style={{ padding: '8px 16px', borderRadius: 8, fontSize: 12, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', cursor: 'pointer', fontFamily: 'inherit' }}>Anulează</button>
              <button onClick={handleGenerate} style={{ padding: '8px 20px', borderRadius: 8, fontSize: 12, fontWeight: 700, border: 'none', background: '#6366f1', color: '#fff', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6 }}>
                <ReloadOutlined /> Generează raport
              </button>
            </div>
          </Form>
        ) : !done ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 16 }}>Se generează raportul…</div>
            <Progress percent={Math.round(Math.min(progress, 99))} strokeColor="#6366f1" trailColor="#eef2ff" style={{ marginBottom: 12 }} />
            <div style={{ fontSize: 12, color: '#94a3b8' }}>
              {progress < 30 ? 'Colectare date senzori…' : progress < 60 ? 'Procesare alerte și conformitate…' : progress < 85 ? 'Generare grafice…' : 'Finalizare…'}
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#d1fae5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 28, color: '#10b981' }}>
              <CheckCircleOutlined />
            </div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>Raport generat!</div>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 20 }}>{newRep?.title} · {newRep?.fileSize}</div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              {newRep && <>
                <button onClick={() => exportPDF(newRep, live)} style={{ padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 700, border: 'none', background: '#ef4444', color: '#fff', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FilePdfOutlined /> PDF
                </button>
                <button onClick={() => exportCSV(newRep, live)} style={{ padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 700, border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#059669', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FileExcelOutlined /> CSV
                </button>
              </>}
              <button onClick={handleClose} style={{ padding: '8px 16px', borderRadius: 8, fontSize: 12, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', cursor: 'pointer', fontFamily: 'inherit' }}>Închide</button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

// ─── Panel principal ──────────────────────────────────────

const ReportsPanel: React.FC = () => {
  const { sensors, alerts } = useSimulatorData();
  const { logActivity }     = useAuth();

  const sss = {
    online:      sensors.filter(s => s.status === 'online').length,
    warning:     sensors.filter(s => s.status === 'warning').length,
    offline:     sensors.filter(s => s.status === 'offline').length,
    calibrating: sensors.filter(s => s.status === 'calibrating').length,
  };
  const live: LiveData = { sensors, alerts, sensorStatusStats: sss };

  const [reports,  setReports]  = useState<Report[]>(mockReports);
  const [genOpen,  setGenOpen]  = useState(false);
  const [typeF,    setTypeF]    = useState('all');
  const [statusF,  setStatusF]  = useState('all');

  const filtered = reports.filter(r =>
    (typeF   === 'all' || r.type   === typeF) &&
    (statusF === 'all' || r.status === statusF)
  );

  const onGenerate = (rep: Report) => {
    setReports(p => [rep, ...p]);
    setTimeout(() => {
      setReports(p => p.map(r => r.id === rep.id ? { ...r, status: 'generated' } : r));
    }, 3000);
    logActivity(`Raport generat: ${rep.title}`, 'Rapoarte');
  };

  const generated = reports.filter(r => r.status === 'generated').length;
  const pending   = reports.filter(r => r.status === 'pending').length;
  const totalMB   = reports.filter(r => r.fileSize !== '–').reduce((s, r) => s + parseFloat(r.fileSize), 0).toFixed(1);

  const alertData = dailyAlertStats.slice(-14);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.4px' }}>Rapoarte</h2>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>{generated} generate · {pending} în curs · {totalMB} MB total</span>
        </div>
        <button onClick={() => setGenOpen(true)} style={{ padding: '9px 18px', borderRadius: 10, fontSize: 12, fontWeight: 700, border: 'none', background: '#6366f1', color: '#fff', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6 }}>
          <PlusOutlined /> Raport nou
        </button>
      </div>

      {/* KPI */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        {[
          { label: 'Generate',   value: generated, color: '#10b981', bg: '#d1fae5', icon: <CheckCircleOutlined /> },
          { label: 'În curs',    value: pending,   color: '#f59e0b', bg: '#fef3c7', icon: <ClockCircleOutlined /> },
          { label: 'Total MB',   value: `${totalMB}`, color: '#6366f1', bg: '#eef2ff', icon: <FileTextOutlined /> },
          { label: 'Personalizate', value: reports.filter(r => r.type === 'custom').length, color: '#f59e0b', bg: '#fef3c7', icon: <SettingOutlined /> },
        ].map((k, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: '14px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: k.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, color: k.color }}>{k.icon}</div>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>{k.label}</span>
            </div>
            <div style={{ fontSize: 26, fontWeight: 900, color: k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Bar chart alerte */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 14 }}>Tendință alerte — ultimele 14 zile</div>
        <ResponsiveContainer width="100%" height={140}>
          <BarChart data={alertData} margin={{ top: 4, right: 8, bottom: 0, left: -28 }} barSize={12}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} interval={2} />
            <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} />
            <RTooltip contentStyle={{ borderRadius: 10, fontSize: 11 }} />
            <Bar dataKey="critical" name="Critic"  stackId="a" fill="#ef4444" />
            <Bar dataKey="high"     name="Ridicat" stackId="a" fill="#f97316" />
            <Bar dataKey="medium"   name="Mediu"   stackId="a" fill="#f59e0b" />
            <Bar dataKey="low"      name="Scăzut"  stackId="a" fill="#84cc16" radius={[2,2,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Lista rapoarte */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', gap: 10, padding: '12px 16px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap', alignItems: 'center' }}>
          <Select value={typeF} onChange={setTypeF} size="small" style={{ width: 160 }}>
            <Option value="all">Toate tipurile</Option>
            {(Object.keys(TYPE_LABEL) as ReportType[]).map(t => <Option key={t} value={t}>{TYPE_LABEL[t]}</Option>)}
          </Select>
          <Select value={statusF} onChange={setStatusF} size="small" style={{ width: 140 }}>
            <Option value="all">Toate statusurile</Option>
            {(Object.keys(STAT_LABEL) as ReportStatus[]).map(s => <Option key={s} value={s}>{STAT_LABEL[s]}</Option>)}
          </Select>
          <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 'auto' }}>{filtered.length} rapoarte</span>
        </div>
        {filtered.map(r => (
          <ReportRow key={r.id} report={r} onPreview={() => {}} live={live} />
        ))}
        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
            <FileTextOutlined style={{ fontSize: 24, display: 'block', marginBottom: 8 }} />
            Niciun raport în categoria selectată
          </div>
        )}
      </div>

      <GenerateModal open={genOpen} onClose={() => setGenOpen(false)} onGenerate={onGenerate} live={live} />
    </div>
  );
};

export default ReportsPanel;